/**
 * Detects and converts bullet-list-with-pipes format to proper markdown tables.
 *
 * Supports two input formats:
 * Format 1 (all lines with dashes):
 * - Header1 | Header2 | Header3
 * - Value1 | Value2 | Value3
 *
 * Format 2 (header without dash, data with dashes):
 * Header1 | Header2 | Header3
 * - Value1 | Value2 | Value3
 *
 * Output format:
 * | Header1 | Header2 | Header3 |
 * |---------|---------|---------|
 * | Value1 | Value2 | Value3 |
 */

/**
 * Helper function to collect consecutive dash-prefixed pipe lines
 * Returns the collected table lines and the index after the last processed line
 */
function collectDashPrefixedLines(
  lines: string[],
  startIndex: number
): { tableLines: string[]; nextIndex: number } {
  const tableLines: string[] = [];
  let j = startIndex;

  while (j < lines.length) {
    const currentLine = lines[j];
    const match = currentLine.match(/^[\s]*-[\s]+(.+\|.+)$/);

    if (match) {
      tableLines.push(match[1].trim());
      j++;
    } else {
      break;
    }
  }

  return { tableLines, nextIndex: j };
}

/**
 * Attempts to extract a Format 2 table (header without dash, data with dashes)
 * Returns the table lines and next index if found, otherwise null
 */
function tryExtractFormat2Table(
  lines: string[],
  currentIndex: number
): { tableLines: string[]; nextIndex: number } | null {
  const line = lines[currentIndex];
  const headerWithoutDash = line.match(/^[\s]*([^-\s].*\|.+)$/);

  if (!headerWithoutDash || currentIndex + 1 >= lines.length) {
    return null;
  }

  const nextLine = lines[currentIndex + 1];
  const nextLineHasDash = /^[\s]*-[\s]+(.+\|.+)$/.test(nextLine);

  if (!nextLineHasDash) {
    return null;
  }

  // Found Format 2 table: collect header and data rows
  const tableLines: string[] = [headerWithoutDash[1].trim()];
  const { tableLines: dataLines, nextIndex } = collectDashPrefixedLines(lines, currentIndex + 1);
  tableLines.push(...dataLines);

  // Validate we have at least header + one data row
  if (tableLines.length < 2) {
    return null;
  }

  return { tableLines, nextIndex };
}

/**
 * Attempts to extract a Format 1 table (all lines with dash prefix)
 * Returns the table lines and next index if found, otherwise null
 */
function tryExtractFormat1Table(
  lines: string[],
  currentIndex: number
): { tableLines: string[]; nextIndex: number } | null {
  const line = lines[currentIndex];
  const bulletTableMatch = line.match(/^[\s]*-[\s]+(.+\|.+)$/);

  if (!bulletTableMatch) {
    return null;
  }

  // Found potential Format 1 table: collect all consecutive bullet-pipe lines
  const { tableLines, nextIndex } = collectDashPrefixedLines(lines, currentIndex);

  // Validate we have at least header + one data row
  if (tableLines.length < 2) {
    return null;
  }

  return { tableLines, nextIndex };
}

function isMarkdownTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|');
}

function isMarkdownTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  return /^\|?(?:\s*:?-{3,}:?\s*\|)+\s*$/.test(trimmed);
}

function parseMarkdownRowCells(row: string): string[] {
  const trimmed = row.trim();
  const normalized = trimmed.replace(/^\|/, '').replace(/\|$/, '');

  return normalized.split('|').map(cell => cell.replace(/\s+/g, ' ').trim());
}

function tryExtractMalformedMarkdownTable(
  lines: string[],
  currentIndex: number
): { tableLines: string[]; nextIndex: number } | null {
  const headerLine = lines[currentIndex];

  if (!isMarkdownTableRow(headerLine)) {
    return null;
  }

  let separatorIndex = currentIndex + 1;
  while (separatorIndex < lines.length && lines[separatorIndex].trim() === '') {
    separatorIndex++;
  }

  if (separatorIndex >= lines.length || !isMarkdownTableSeparator(lines[separatorIndex])) {
    return null;
  }

  const headerCells = parseMarkdownRowCells(headerLine);
  if (headerCells.length < 2) {
    return null;
  }

  let contiguousIndex = separatorIndex + 1;
  let hasContiguousDataRows = false;
  while (contiguousIndex < lines.length && isMarkdownTableRow(lines[contiguousIndex])) {
    hasContiguousDataRows = true;
    contiguousIndex++;
  }

  if (hasContiguousDataRows) {
    const nextLine = lines[contiguousIndex];
    if (
      contiguousIndex >= lines.length ||
      nextLine.trim() === '' ||
      !nextLine.includes('|')
    ) {
      return null;
    }
  }

  const tableLines: string[] = [
    '| ' + headerCells.join(' | ') + ' |',
    '|' + headerCells.map(() => '---').join('|') + '|',
  ];

  let i = separatorIndex + 1;
  let buffer = '';
  let sawDataRow = false;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      if (!buffer) {
        const nextNonEmptyLine = lines.slice(i + 1).find(candidate => candidate.trim() !== '');
        if (!nextNonEmptyLine || !nextNonEmptyLine.includes('|')) {
          break;
        }
      }
      i++;
      continue;
    }

    if (!buffer && !trimmed.includes('|')) {
      break;
    }

    buffer = buffer ? `${buffer} ${trimmed}` : trimmed;

    const cells = parseMarkdownRowCells(buffer);
    if (cells.length >= headerCells.length) {
      const normalizedCells = cells.slice(0, headerCells.length);
      tableLines.push(`| ${normalizedCells.join(' | ')} |`);
      sawDataRow = true;
      buffer = '';
    }

    i++;
  }

  if (!sawDataRow) {
    return null;
  }

  if (buffer.trim().length > 0) {
    return null;
  }

  return { tableLines, nextIndex: i };
}

export function convertBulletTablestoMarkdown(content: string): string {
  if (!content) return content;

  const lines = content.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const malformedMarkdownTable = tryExtractMalformedMarkdownTable(lines, i);
    if (malformedMarkdownTable) {
      result.push(malformedMarkdownTable.tableLines.join('\n'));
      i = malformedMarkdownTable.nextIndex;
      continue;
    }

    // Try to extract Format 2 table (header without dash, data with dashes)
    const format2Table = tryExtractFormat2Table(lines, i);
    if (format2Table) {
      const markdownTable = convertToMarkdownTable(format2Table.tableLines);
      result.push(markdownTable);
      i = format2Table.nextIndex;
      continue;
    }

    // Try to extract Format 1 table (all lines with dash prefix)
    const format1Table = tryExtractFormat1Table(lines, i);
    if (format1Table) {
      const markdownTable = convertToMarkdownTable(format1Table.tableLines);
      result.push(markdownTable);
      i = format1Table.nextIndex;
      continue;
    }

    // Not a table line, keep as is
    result.push(lines[i]);
    i++;
  }

  return result.join('\n');
}

function convertToMarkdownTable(rows: string[]): string {
  if (rows.length === 0) return '';

  // Parse all rows to get cell values
  const parsedRows = rows.map(row =>
    row.split('|').map(cell => cell.trim())
  );

  // Determine the maximum number of columns
  const maxCols = Math.max(...parsedRows.map(row => row.length));

  // Normalize all rows to have the same number of columns
  const normalizedRows = parsedRows.map(row => {
    while (row.length < maxCols) {
      row.push('');
    }
    return row;
  });

  // Build markdown table
  const tableLines: string[] = [];

  // Header row (first row)
  const headerRow = '| ' + normalizedRows[0].join(' | ') + ' |';
  tableLines.push(headerRow);

  // Separator row
  const separatorRow = '|' + normalizedRows[0].map(() => '---').join('|') + '|';
  tableLines.push(separatorRow);

  // Data rows (remaining rows)
  for (let i = 1; i < normalizedRows.length; i++) {
    const dataRow = '| ' + normalizedRows[i].join(' | ') + ' |';
    tableLines.push(dataRow);
  }

  return tableLines.join('\n');
}
